/*******************************************************************
 * ADOBE CONFIDENTIAL
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import {breakpoint} from '../../breakpoints/breakpoint-macro' with {type: 'macro'};
import {Skeleton} from '../utils/Skeleton';
import {HomeHeroSkeleton} from '../HomeSkeleton/HomeSkeleton';

export class DXCardSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				padding: {default: 12, [breakpoint('m')]: 16},
				gap: {default: 12, [breakpoint('m')]: '[26px]'},
				borderRadius: '[16px]',
				backgroundColor: '[var(--ah-skl-layer-2)]',
				minHeight: {
					default: 172,
					[breakpoint('m')]: 228,
				},
				width: {
					default: 'full',
					[breakpoint('m')]: 252,
				},
				minWidth: {
					default: 0,
					[breakpoint('m')]: 180,
				},
				maxWidth: {
					default: 'full',
					[breakpoint('s')]: 'full',
					[breakpoint('m')]: 280,
					[breakpoint('xl')]: 260,
					[breakpoint('xxl')]: 320,
				},
				boxSizing: 'border-box',
				overflow: 'hidden',
				flexShrink: {
					[breakpoint('m')]: 1,
				},
			}),
			children: `
				<div class="${style({flexShrink: 0})}">
					<div class="${style({
						maxWidth: {default: 75, [breakpoint('m')]: 120},
						height: {default: 50, [breakpoint('m')]: 80},
						borderRadius: {
							default: '[6px]',
							[breakpoint('m')]: '[10px]',
						},
					})}">
						${this.image({aspect: {x: 3, y: 2}})}
					</div>
				</div>
				<div class="${style({
					boxSizing: 'border-box',
					paddingEnd: 48,
				})}">
					<div>
						${this.paragraph()}
					</div>
				</div>
			`,
		});
	}
}

export class DXCardGridSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				gap: 32,
				flexGrow: 1,
				flexShrink: 0,
				paddingX: {
					default: 20,
					[breakpoint('m')]: 32,
				},
				paddingTop: '[26px]',
				paddingBottom: {
					default: '[26px]',
					[breakpoint('m')]: '[72px]',
				},
				boxSizing: 'border-box',
				overflow: 'hidden',
				minWidth: 0,
			}),
			children: `
				<div class="${style({
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'stretch',
					justifyContent: 'start',
					minWidth: 0,
				})}">
					<div class="${style({
						display: {
							default: 'grid',
							[breakpoint('s')]: 'grid',
							[breakpoint('m')]: 'flex',
						},
						gridTemplateColumns: {
							default: '1fr',
							[breakpoint('s')]: 'repeat(2, 1fr)',
						},
						flexDirection: {
							[breakpoint('m')]: 'row',
						},
						alignItems: {
							[breakpoint('m')]: 'stretch',
						},
						justifyContent: {
							[breakpoint('m')]: 'center',
						},
						gap: {
							default: 20,
						},
						width: 'full',
						minWidth: 0,
						paddingX: {
							default: 0,
							[breakpoint('l')]: 16,
						},
						marginX: {
							default: 0,
							[breakpoint('s')]: 'auto',
						},
						maxWidth: {
							default: 'full',
							[breakpoint('s')]: 800,
							[breakpoint('m')]: 1200,
						},
						overflow: {
							default: 'hidden',
							[breakpoint('m')]: 'hidden',
						},
						flexWrap: {
							[breakpoint('m')]: 'nowrap',
						},
					})}">
						${this.repeat(4, new DXCardSkeleton(this.props).toString())}
					</div>
				</div>
			`,
		});
	}
}

export class DXSkeleton extends Skeleton {
	public toString(): string {
		return this.wrapper({
			className: style({
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'start',
				flexGrow: 1,
				flexShrink: 0,
				minWidth: 0,
				minHeight: 0,
				backgroundColor: '[var(--ah-skl-layer-1)]',
				borderTopRadius: {
					default: 'none',
					[breakpoint('m')]: 'xl',
				},
				height: 'full',
			}),
			children: `
				${new HomeHeroSkeleton(this.props).toString()}
				${new DXCardGridSkeleton(this.props).toString()}
			`,
		});
	}
}
